import { useEffect, useMemo, useRef, useState } from "react";

function clamp01(n) {
  if (Number.isNaN(n) || n === null || n === undefined) return 0;
  return Math.max(0, Math.min(1, Number(n)));
}

function arraysEqual(a, b) {
  if (a === b) return true;
  if (!a || !b) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

export const DEFAULT_PARTICIPANT_RANKING_CONFIG = Object.freeze({
  tickIntervalMs: 500, // Update more frequently for smoother transitions
  reorderIntervalMs: 300, // Reorder more frequently like Zoom
  transitionQueueMs: 150, // Process queue every 150ms for smooth transitions
  topParticipantsUpdateMs: 60 * 1000, // Update top 5 participants every 1 minute for better UX
  topParticipantsCount: 5, // Number of top participants to update
  maxMoveDistance: 2, // Maximum positions a participant can move per transition (prevents jumps)
  alpha: 0.3, // Smoothing factor for volume
  speakingThreshold: 0.1, // Threshold to consider someone speaking
  recentSpeakerMs: 5000, // Time window for recent speakers (Zoom uses ~5 seconds)
  recentSpeakerDecayMs: 10000, // Gradual decay after recent speaker window
  frequentSpeakerThreshold: 10, // Minimum times to speak to get a point
  frequentSpeakerWindowMs: 2 * 60 * 1000, // 2 minutes window for frequent speaker tracking
  videoWeight: 1, // Video on gets priority
  pinnedWeight: 2, // Pinned participants get higher priority
});


export class ParticipantRanker {
  constructor(config = {}) {
    this._config = { ...DEFAULT_PARTICIPANT_RANKING_CONFIG, ...config };
    this._states = new Map(); // id -> state
    this._lastReorderAt = 0;
    this._activeSpeakerId = null;
  }

  setConfig(partialConfig = {}) {
    this._config = { ...this._config, ...partialConfig };
  }

  syncIds(ids, now) {
    const nextIds = Array.isArray(ids) ? ids : [];
    const keep = new Set(nextIds);

    // Remove departed
    for (const id of this._states.keys()) {
      if (!keep.has(id)) this._states.delete(id);
    }

    // Add new
    for (let i = 0; i < nextIds.length; i++) {
      const id = nextIds[i];
      if (!id) continue;
      if (!this._states.has(id)) {
        this._states.set(id, {
          id,
          hasVideo: false,
          isPinned: false,
          isSelf: false,
          currentVolume: 0,
          smoothedVolume: 0,
          lastSpokeAt: 0,
          joinedAt: now,
          score: 0,
          rank: this._states.size + i, // place new at bottom
          speakingEvents: [], // Array of timestamps when participant spoke
          frequentSpeakerPoints: 0, // Accumulated points for frequent speaking
          lastFrequentSpeakerCheck: 0, // Last time we checked for frequent speaker points
        });
      }
    }
  }

  setParticipantMeta(id, meta = {}) {
    const s = this._states.get(id);
    if (!s) return;
    if (typeof meta.hasVideo === "boolean") s.hasVideo = meta.hasVideo;
    if (typeof meta.isPinned === "boolean") s.isPinned = meta.isPinned;
    if (typeof meta.isSelf === "boolean") s.isSelf = meta.isSelf;
  }


  setVolume(id, volume, now) {
    const s = this._states.get(id);
    if (!s) return;
    const v = clamp01(volume);
    const wasSpeaking = s.currentVolume >= this._config.speakingThreshold;
    s.currentVolume = v;

    // Track speaking events - record when participant starts speaking
    if (v >= this._config.speakingThreshold && !wasSpeaking) {
      s.lastSpokeAt = now;
      // Record speaking event
      s.speakingEvents.push(now);

      // Clean up old events (older than the window)
      const windowStart = now - this._config.frequentSpeakerWindowMs;
      s.speakingEvents = s.speakingEvents.filter(timestamp => timestamp >= windowStart);

      // Check if participant qualifies for frequent speaker points
      this._checkFrequentSpeaker(s, now);
    }
  }

  _checkFrequentSpeaker(state, now) {
    const { frequentSpeakerThreshold, frequentSpeakerWindowMs } = this._config;
    const windowStart = now - frequentSpeakerWindowMs;

    // Count speaking events in the last 2 minutes
    const recentEvents = state.speakingEvents.filter(timestamp => timestamp >= windowStart);

    // If participant has spoken at least the threshold times, award a point
    if (recentEvents.length >= frequentSpeakerThreshold) {
      // Only award a new point if enough time has passed since last check
      // This prevents awarding multiple points for the same qualifying period
      const timeSinceLastCheck = now - (state.lastFrequentSpeakerCheck || 0);
      const checkInterval = frequentSpeakerWindowMs / 2; // Check every minute

      if (timeSinceLastCheck >= checkInterval) {
        state.frequentSpeakerPoints += 1;
        state.lastFrequentSpeakerCheck = now;
      }
    }
  }

  setActiveSpeaker(activeSpeakerId, now) {
    const prev = this._activeSpeakerId;
    this._activeSpeakerId = activeSpeakerId || null;
    if (prev && prev !== this._activeSpeakerId) {
      const prevState = this._states.get(prev);
      if (prevState) prevState.currentVolume = 0;
    }
    if (this._activeSpeakerId) {
      this.setVolume(this._activeSpeakerId, 1, now);
    }
  }

  // Legacy method kept for compatibility, but not used in Zoom-style ranking
  _computeScore(state, now) {
    // This method is no longer used in the Zoom-style ranking algorithm
    // but kept for backward compatibility
    return 0;
  }


  tick(now) {
    const {
      alpha,
      reorderIntervalMs,
      recentSpeakerMs,
      recentSpeakerDecayMs,
      frequentSpeakerWindowMs,
    } = this._config;

    // Update smoothed volume for all participants and clean up old speaking events
    for (const s of this._states.values()) {
      const cur = clamp01(s.currentVolume);
      const prev = clamp01(s.smoothedVolume);
      s.smoothedVolume = alpha * cur + (1 - alpha) * prev;

      // Clean up old speaking events outside the window
      const windowStart = now - frequentSpeakerWindowMs;
      s.speakingEvents = s.speakingEvents.filter(timestamp => timestamp >= windowStart);
    }

    const ids = Array.from(this._states.keys());
    if (ids.length === 0) {
      return { orderedIds: [], dominantId: null };
    }

    // Zoom-like ranking: Use tiered approach instead of score-based
    const states = ids
      .map((id) => this._states.get(id))
      .filter(Boolean);

    // Only reorder if enough time has passed
    const shouldReorder = now - this._lastReorderAt >= reorderIntervalMs;

    if (!shouldReorder) {
      // Return current order without reordering
      const orderedIds = states
        .sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0))
        .map((s) => s.id);
      const dominantId = orderedIds[0] || null;
      return { orderedIds, dominantId };
    }

    // Zoom-style ranking algorithm
    states.sort((a, b) => {
      // Tier 1: Frequent speaker points (participants who speak frequently get priority)
      const aFrequentPoints = a.frequentSpeakerPoints || 0;
      const bFrequentPoints = b.frequentSpeakerPoints || 0;
      if (aFrequentPoints !== bFrequentPoints) {
        return bFrequentPoints - aFrequentPoints; // Higher points first
      }

      // Tier 2: Pinned participants
      if (a.isPinned && !b.isPinned) return -1;
      if (!a.isPinned && b.isPinned) return 1;

      // Tier 4: Recent speakers (prioritize those who spoke recently)
      const aTimeSinceSpoke = now - (a.lastSpokeAt || 0);
      const bTimeSinceSpoke = now - (b.lastSpokeAt || 0);

      const aIsRecent = aTimeSinceSpoke < recentSpeakerMs;
      const bIsRecent = bTimeSinceSpoke < recentSpeakerMs;

      if (aIsRecent && !bIsRecent) return -1;
      if (!aIsRecent && bIsRecent) return 1;

      // If both are recent, prioritize the one who spoke more recently
      if (aIsRecent && bIsRecent) {
        return aTimeSinceSpoke - bTimeSinceSpoke;
      }

      // If neither is recent but one spoke more recently, prioritize that one
      // (for gradual decay after the recent window)
      if (!aIsRecent && !bIsRecent && a.lastSpokeAt && b.lastSpokeAt) {
        const aDecayTime = aTimeSinceSpoke - recentSpeakerMs;
        const bDecayTime = bTimeSinceSpoke - recentSpeakerMs;
        // Only consider decay if within decay window
        if (aDecayTime < recentSpeakerDecayMs && bDecayTime < recentSpeakerDecayMs) {
          return aTimeSinceSpoke - bTimeSinceSpoke;
        }
      }

      // Tier 5: Video status (video on > video off)
      if (a.hasVideo && !b.hasVideo) return -1;
      if (!a.hasVideo && b.hasVideo) return 1;

      // Tier 6: Current speaking volume (for tie-breaking)
      const volumeDiff = (b.smoothedVolume || 0) - (a.smoothedVolume || 0);
      if (Math.abs(volumeDiff) > 0.01) return volumeDiff;

      // Tier 7: Join order (earlier joiners stay higher, newer at bottom)
      return (a.joinedAt || 0) - (b.joinedAt || 0);
    });

    const orderedIds = states.map((s) => s.id);

    // Update ranks
    for (let i = 0; i < orderedIds.length; i++) {
      const st = this._states.get(orderedIds[i]);
      if (st) st.rank = i;
    }

    this._lastReorderAt = now;

    const dominantId = orderedIds[0] || null;
    return { orderedIds, dominantId };
  }
}


// Smooth transition queue for participant reordering
class TransitionQueue {
  constructor(config) {
    this._config = config;
    this._queue = [];
    this._currentOrder = [];
    this._targetOrder = [];
    this._lastProcessTime = 0;
    this._lastTopUpdateTime = 0;
  }

  enqueue(targetOrder, dominantId) {
    this._targetOrder = targetOrder;
    this._dominantId = dominantId;
  }

  process(currentOrder, now) {
    if (this._targetOrder.length === 0) {
      return { orderedIds: currentOrder, changed: false };
    }

    const timeSinceLastProcess = now - this._lastProcessTime;
    if (timeSinceLastProcess < this._config.transitionQueueMs) {
      return { orderedIds: currentOrder, changed: false };
    }

    this._lastProcessTime = now;

    // If current order is empty or completely different, apply target immediately
    if (currentOrder.length === 0 || this._arraysCompletelyDifferent(currentOrder, this._targetOrder)) {
      this._currentOrder = [...this._targetOrder];
      this._lastTopUpdateTime = now;
      return { orderedIds: this._currentOrder, changed: true };
    }

    // Check if it's time to update top participants
    const timeSinceTopUpdate = now - this._lastTopUpdateTime;
    const shouldUpdateTop = timeSinceTopUpdate >= this._config.topParticipantsUpdateMs;

    if (shouldUpdateTop) {
      // Update only top N participants, keep the rest stable
      const newOrder = this._updateTopParticipants(currentOrder, this._targetOrder);
      const changed = !arraysEqual(currentOrder, newOrder);
      this._currentOrder = newOrder;
      this._lastTopUpdateTime = now;
      return { orderedIds: this._currentOrder, changed };
    } else {
      // Don't change anything if it's not time to update top participants
      return { orderedIds: currentOrder, changed: false };
    }
  }

  _updateTopParticipants(currentOrder, targetOrder) {
    const topCount = this._config.topParticipantsCount || 5;
    const result = [...currentOrder];

    // Get top N from current and target orders
    const currentTop = currentOrder.slice(0, topCount);
    const targetTop = targetOrder.slice(0, topCount);

    // Check if top 5 participants are the same set (same IDs, possibly different order)
    const currentTopSet = new Set(currentTop);
    const targetTopSet = new Set(targetTop);
    const sameSet = currentTopSet.size === targetTopSet.size &&
      [...currentTopSet].every(id => targetTopSet.has(id));

    if (sameSet && currentTop.length > 0 && targetTop.length > 0) {
      // Same participants, different order - only update the first position
      const targetFirst = targetTop[0];
      const currentFirst = currentTop[0];

      if (targetFirst !== currentFirst) {
        // Move target first participant to position 0
        const currentIndex = result.indexOf(targetFirst);
        if (currentIndex > 0) {
          result.splice(currentIndex, 1);
          result.splice(0, 0, targetFirst);
        }
      }
    } else {
      // Different participants in top 5 - update all top positions
      // Build a map of current positions
      const currentIndexMap = new Map();
      result.forEach((id, idx) => currentIndexMap.set(id, idx));

      // Update top positions: move participants from target top to their correct positions
      for (let i = 0; i < targetTop.length && i < topCount; i++) {
        const targetId = targetTop[i];
        const currentIndex = currentIndexMap.get(targetId);

        // If participant is not at the correct top position, move them
        if (currentIndex !== i) {
          // Remove from current position if it exists
          if (currentIndex !== undefined && currentIndex >= 0) {
            result.splice(currentIndex, 1);
            // Update index map for remaining items
            currentIndexMap.clear();
            result.forEach((id, idx) => currentIndexMap.set(id, idx));
          }

          // Insert at correct top position
          result.splice(i, 0, targetId);
          // Update index map
          currentIndexMap.set(targetId, i);
          // Rebuild map for items after insertion
          for (let j = i + 1; j < result.length; j++) {
            currentIndexMap.set(result[j], j);
          }
        }
      }
    }

    return result;
  }

  _arraysCompletelyDifferent(a, b) {
    if (a.length !== b.length) return true;
    const setA = new Set(a);
    const setB = new Set(b);
    if (setA.size !== setB.size) return true;
    for (const item of setA) {
      if (!setB.has(item)) return true;
    }
    return false;
  }

  _smoothTransition(currentOrder, targetOrder) {
    const maxMove = this._config.maxMoveDistance || 2;
    const result = [...currentOrder];
    const targetIndexMap = new Map();
    const targetSet = new Set(targetOrder);

    // Build target index map
    targetOrder.forEach((id, idx) => targetIndexMap.set(id, idx));

    // Remove participants that are no longer in target
    for (let i = result.length - 1; i >= 0; i--) {
      if (!targetSet.has(result[i])) {
        result.splice(i, 1);
      }
    }

    // Create a list of moves needed (calculate from current result state)
    const moves = [];
    const currentIndexMap = new Map();
    result.forEach((id, idx) => currentIndexMap.set(id, idx));

    for (let i = 0; i < result.length; i++) {
      const id = result[i];
      const currentIdx = currentIndexMap.get(id);
      const targetIdx = targetIndexMap.get(id);

      if (targetIdx === undefined) continue; // Shouldn't happen after filtering
      if (currentIdx === targetIdx) continue; // Already in correct position

      const distance = targetIdx - currentIdx;
      if (Math.abs(distance) > maxMove) {
        // Limit movement distance
        const limitedDistance = distance > 0 ? maxMove : -maxMove;
        moves.push({ id, from: currentIdx, to: currentIdx + limitedDistance });
      } else {
        moves.push({ id, from: currentIdx, to: targetIdx });
      }
    }

    // Handle new participants (not in current order)
    for (const id of targetOrder) {
      if (!currentIndexMap.has(id)) {
        const targetIdx = targetIndexMap.get(id);
        // Insert new participants at their target position (or as close as possible)
        const insertIdx = Math.min(targetIdx, result.length);
        moves.push({ id, from: -1, to: insertIdx, isNew: true });
      }
    }

    // Sort moves by priority: active speaker first, then by target position
    moves.sort((a, b) => {
      // Active speaker moves first
      const aIsActive = a.id === this._dominantId;
      const bIsActive = b.id === this._dominantId;
      if (aIsActive && !bIsActive) return -1;
      if (!aIsActive && bIsActive) return 1;

      // Then by target position
      return a.to - b.to;
    });

    // Apply moves one at a time, updating indices as we go
    for (const move of moves) {
      const currentPos = result.indexOf(move.id);
      if (currentPos === -1 && move.from === -1) {
        // New participant - insert at target position
        const insertIdx = Math.min(move.to, result.length);
        result.splice(insertIdx, 0, move.id);
      } else if (currentPos >= 0) {
        // Existing participant - move toward target
        result.splice(currentPos, 1);
        const insertIdx = Math.min(move.to, result.length);
        result.splice(insertIdx, 0, move.id);
      }
    }

    return result;
  }

  reset() {
    this._queue = [];
    this._currentOrder = [];
    this._targetOrder = [];
    this._lastProcessTime = 0;
  }
}

export function useParticipantRanking({
  participantIds,
  participants,
  activeSpeakerId,
  pinnedIds,
  selfId,
  volumeById,
  enabled = true,
  config,
} = {}) {
  const mergedConfig = useMemo(() => {
    return { ...DEFAULT_PARTICIPANT_RANKING_CONFIG, ...(config || {}) };
  }, [config]);

  const rankerRef = useRef(null);
  const transitionQueueRef = useRef(null);
  const inputsRef = useRef({
    participantIds: [],
    participants: null,
    activeSpeakerId: null,
    pinnedIds: [],
    selfId: null,
    volumeById: null,
    enabled: true,
  });

  const [orderedIds, setOrderedIds] = useState(() =>
    Array.isArray(participantIds) ? participantIds : []
  );
  const [dominantId, setDominantId] = useState(null);
  const orderedIdsRef = useRef(orderedIds);

  useEffect(() => {
    inputsRef.current = {
      participantIds: Array.isArray(participantIds) ? participantIds : [],
      participants: participants || null,
      activeSpeakerId: activeSpeakerId || null,
      pinnedIds: pinnedIds || [],
      selfId: selfId || null,
      volumeById: volumeById || null,
      enabled: !!enabled,
    };
  }, [participantIds, participants, activeSpeakerId, pinnedIds, selfId, volumeById, enabled]);

  // Keep ranker config up to date.
  useEffect(() => {
    if (!rankerRef.current) {
      rankerRef.current = new ParticipantRanker(mergedConfig);
      transitionQueueRef.current = new TransitionQueue(mergedConfig);
      return;
    }
    rankerRef.current.setConfig(mergedConfig);
    if (transitionQueueRef.current) {
      transitionQueueRef.current._config = mergedConfig;
    }
  }, [mergedConfig]);


  useEffect(() => {
    if (!enabled) {
      const ids = Array.isArray(participantIds) ? participantIds : [];
      orderedIdsRef.current = ids;
      setOrderedIds(ids);
      setDominantId(activeSpeakerId || ids[0] || null);
      if (transitionQueueRef.current) {
        transitionQueueRef.current.reset();
      }
    }
  }, [enabled, participantIds, activeSpeakerId]);

  useEffect(() => {
    if (!enabled) {
      if (transitionQueueRef.current) {
        transitionQueueRef.current.reset();
      }
      return;
    }

    const tick = () => {
      const now = Date.now();
      const ranker = rankerRef.current;
      const transitionQueue = transitionQueueRef.current;
      const inp = inputsRef.current;
      if (!ranker || !transitionQueue || !inp.enabled) return;

      ranker.syncIds(inp.participantIds, now);

      const pinnedSet = new Set(
        Array.isArray(inp.pinnedIds)
          ? inp.pinnedIds
          : inp.pinnedIds
            ? Array.from(inp.pinnedIds)
            : []
      );

      for (const id of inp.participantIds) {
        const p = inp.participants?.get ? inp.participants.get(id) : null;
        ranker.setParticipantMeta(id, {
          hasVideo: !!p?.webcamOn,
          isPinned: pinnedSet.has(id),
          isSelf: inp.selfId ? id === inp.selfId : false,
        });
      }

      if (inp.volumeById && typeof inp.volumeById === "object") {
        for (const id of inp.participantIds) {
          const vol = inp.volumeById[id];
          if (vol !== undefined) ranker.setVolume(id, vol, now);
        }

        if (inp.activeSpeakerId) ranker.setActiveSpeaker(inp.activeSpeakerId, now);
      } else {
        ranker.setActiveSpeaker(inp.activeSpeakerId, now);
      }

      // Get target order from ranker
      const res = ranker.tick(now);

      // Update dominant ID immediately (no transition needed)
      setDominantId((prev) => {
        if (prev !== res.dominantId) {
          transitionQueue._dominantId = res.dominantId;
          return res.dominantId;
        }
        return prev;
      });

      // Queue the target order for smooth transition
      transitionQueue.enqueue(res.orderedIds, res.dominantId);
    };

    const processTransition = () => {
      const now = Date.now();
      const transitionQueue = transitionQueueRef.current;
      if (!transitionQueue) return;

      // Process transition queue using ref to avoid dependency issues
      const currentOrder = orderedIdsRef.current;
      const { orderedIds: newOrder, changed } = transitionQueue.process(currentOrder, now);

      if (changed) {
        orderedIdsRef.current = newOrder;
        setOrderedIds(newOrder);
      }
    };

    // Update ref when orderedIds changes externally
    orderedIdsRef.current = orderedIds;

    // Initial tick
    tick();

    // Set up intervals
    const tickHandle = setInterval(tick, mergedConfig.tickIntervalMs);
    const transitionHandle = setInterval(processTransition, mergedConfig.transitionQueueMs);

    return () => {
      clearInterval(tickHandle);
      clearInterval(transitionHandle);
    };
  }, [enabled, mergedConfig.tickIntervalMs, mergedConfig.transitionQueueMs]);

  return { orderedIds, dominantId };
}

